<template>
  <div class="promotion-page">
    <header class="promotion-header">
      <div>
        <p class="eyebrow">Manufacturer Promotions</p>
        <h1>프로모션 통합 관리</h1>
        <p class="description">
          제조사별 프로모션 현황을 한 화면에서 조회하고 운영 데이터로 다운로드합니다.
        </p>
      </div>
      <div class="header-actions">
        <a-button @click="resetFilters">
          <i class="fa-solid fa-rotate-right" />
          초기화
        </a-button>
        <a-button :loading="promotionExporting" @click="downloadPromotions">
          <i class="fa-solid fa-file-arrow-down" />
          목록 다운로드
        </a-button>
        <a-button
          type="primary"
          :loading="entryExporting"
          :disabled="selectedPromotionKeys.length === 0"
          @click="downloadSelectedEntries"
        >
          <i class="fa-solid fa-check-to-slot" />
          선택 참여자 다운로드
        </a-button>
        <a-button
          :loading="entryExporting"
          :disabled="!hasScopedEntryFilter"
          @click="downloadScopedEntries"
        >
          <i class="fa-solid fa-filter" />
          조건 참여자 다운로드
        </a-button>
      </div>
    </header>

    <section class="filter-panel">
      <div class="filter-grid">
        <label class="filter-field filter-keyword">
          <span>검색</span>
          <a-input-search
            v-model:value="filters.keyword"
            placeholder="프로모션명, 제조사명, 슬러그 검색"
            allow-clear
            enter-button
            @search="search"
          />
        </label>
        <label class="filter-field">
          <span>제조사</span>
          <a-select
            v-model:value="filters.manufacturerId"
            show-search
            allow-clear
            :filter-option="false"
            placeholder="전체 제조사"
            @search="loadManufacturers"
            @change="search"
          >
            <a-select-option
              v-for="manufacturer in manufacturers"
              :key="manufacturer._id"
              :value="manufacturer._id"
            >
              {{ manufacturer.name }}
            </a-select-option>
          </a-select>
        </label>
        <label class="filter-field">
          <span>상태</span>
          <a-select
            v-model:value="filters.status"
            allow-clear
            placeholder="전체 상태"
            @change="search"
          >
            <a-select-option value="draft">초안</a-select-option>
            <a-select-option value="active">진행중</a-select-option>
            <a-select-option value="paused">일시중지</a-select-option>
            <a-select-option value="closed">종료</a-select-option>
          </a-select>
        </label>
        <label class="filter-field filter-date">
          <span>생성일</span>
          <a-range-picker
            v-model:value="createdRange"
            format="YYYY-MM-DD"
            @change="search"
          />
        </label>
      </div>
      <div class="filter-note">
        프로모션을 체크하면 선택 참여자 다운로드가 활성화됩니다. 조건 다운로드는 제조사 또는 생성일 범위 기준입니다.
      </div>
    </section>

    <section class="table-panel">
      <div class="table-summary">
        <div>
          <strong>{{ total.toLocaleString() }}</strong>
          <span>개의 프로모션</span>
        </div>
        <div class="selected-count" :class="{ active: selectedPromotionKeys.length > 0 }">
          선택 {{ selectedPromotionKeys.length.toLocaleString() }}개
        </div>
      </div>
      <a-table
        :columns="columns"
        :data-source="items"
        :loading="loading"
        :row-key="(record) => record._id"
        :pagination="paginationConfig"
        :row-selection="rowSelection"
        :custom-row="customRow"
        table-layout="fixed"
        @change="handleTableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'title'">
            <div class="title-cell">
              <strong>{{ record.title || "-" }}</strong>
              <span>{{ record.publicSlug || record.qrToken || "-" }}</span>
            </div>
          </template>
          <template v-else-if="column.key === 'status'">
            <a-tag :color="statusColor(record.status)">
              {{ statusLabel(record.status) }}
            </a-tag>
          </template>
          <template v-else-if="column.key === 'leadCount'">
            <span class="number-cell">{{ (record.leadCount || 0).toLocaleString() }}</span>
          </template>
          <template v-else-if="column.key === 'createdAt'">
            {{ formatDate(record.createdAt) }}
          </template>
          <template v-else-if="column.key === 'actions'">
            <a-button size="small" @click.stop="goEntries(record)">
              참여자
            </a-button>
          </template>
        </template>
      </a-table>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useRouter } from "vue-router";
import { message, Modal } from "ant-design-vue";
import dayjs from "dayjs";
import {
  PromotionManagementApi,
  saveExcelBlob,
} from "./api";

const router = useRouter();

const loading = ref(false);
const promotionExporting = ref(false);
const entryExporting = ref(false);
const items = ref([]);
const total = ref(0);
const manufacturers = ref([]);
const createdRange = ref([]);
const selectedPromotionKeys = ref([]);
const pagination = reactive({
  page: 1,
  size: 20,
});
const filters = reactive({
  keyword: "",
  manufacturerId: undefined,
  status: undefined,
});

const columns = [
  {
    title: "제조사",
    dataIndex: "manufacturerName",
    key: "manufacturerName",
    width: 180,
    ellipsis: true,
  },
  {
    title: "프로모션",
    dataIndex: "title",
    key: "title",
    width: 320,
    ellipsis: true,
  },
  {
    title: "상태",
    dataIndex: "status",
    key: "status",
    width: 110,
  },
  {
    title: "참여자",
    dataIndex: "leadCount",
    key: "leadCount",
    width: 110,
    align: "right",
  },
  {
    title: "생성일",
    dataIndex: "createdAt",
    key: "createdAt",
    width: 160,
  },
  {
    title: "관리",
    key: "actions",
    width: 110,
    align: "center",
  },
];

const paginationConfig = computed(() => ({
  current: pagination.page,
  pageSize: pagination.size,
  total: total.value,
  showSizeChanger: true,
  pageSizeOptions: ["20", "50", "100"],
  showTotal: (value) => `총 ${value.toLocaleString()}건`,
}));

const rowSelection = computed(() => ({
  selectedRowKeys: selectedPromotionKeys.value,
  preserveSelectedRowKeys: true,
  onChange: (keys) => {
    selectedPromotionKeys.value = keys;
  },
}));

const requestParams = () => ({
  page: pagination.page,
  size: pagination.size,
  keyword: filters.keyword,
  manufacturerId: filters.manufacturerId,
  status: filters.status,
  createdFrom: createdRange.value?.[0]?.format("YYYY-MM-DD"),
  createdTo: createdRange.value?.[1]?.format("YYYY-MM-DD"),
});

const hasScopedEntryFilter = computed(
  () => !!filters.manufacturerId || !!createdRange.value?.[0] || !!createdRange.value?.[1],
);

const fetchPromotions = async () => {
  loading.value = true;
  try {
    const response = await PromotionManagementApi.getPromotions(requestParams());
    const data = response.data?.data || {};
    items.value = data.items || [];
    total.value = data.total || 0;
    pagination.page = data.page || pagination.page;
    pagination.size = data.size || pagination.size;
  } catch (error) {
    message.error("프로모션 목록을 불러오지 못했습니다.");
  } finally {
    loading.value = false;
  }
};

const loadManufacturers = async (keyword = "") => {
  try {
    const response = await PromotionManagementApi.getManufacturers(keyword);
    manufacturers.value = response.data?.docs || [];
  } catch (error) {
    manufacturers.value = [];
  }
};

const search = () => {
  pagination.page = 1;
  selectedPromotionKeys.value = [];
  fetchPromotions();
};

const resetFilters = () => {
  filters.keyword = "";
  filters.manufacturerId = undefined;
  filters.status = undefined;
  createdRange.value = [];
  search();
};

const handleTableChange = (pageInfo) => {
  pagination.page = pageInfo.current || 1;
  pagination.size = pageInfo.pageSize || 20;
  fetchPromotions();
};

const goEntries = (record) => {
  router.push({
    name: "promotionEntries",
    params: { promotionId: record._id },
    query: {
      title: record.title,
      manufacturerName: record.manufacturerName,
    },
  });
};

const customRow = (record) => ({
  onClick: () => goEntries(record),
});

const downloadPromotions = async () => {
  promotionExporting.value = true;
  try {
    const response = await PromotionManagementApi.exportPromotions(requestParams());
    saveExcelBlob(response, `프로모션_목록_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`);
    message.success("프로모션 목록 다운로드를 시작했습니다.");
  } catch (error) {
    message.error(error.response?.data?.message || "목록 다운로드에 실패했습니다.");
  } finally {
    promotionExporting.value = false;
  }
};

const downloadSelectedEntries = () => {
  if (selectedPromotionKeys.value.length === 0) {
    message.warning("다운로드할 프로모션을 선택해주세요.");
    return;
  }
  Modal.confirm({
    title: "선택 프로모션 참여자 다운로드",
    content:
      "체크한 프로모션의 참여자를 하나의 엑셀로 다운로드합니다. 전화번호와 선택 동의 항목이 포함됩니다.",
    okText: "다운로드",
    cancelText: "취소",
    onOk: async () => {
      entryExporting.value = true;
      try {
        const response = await PromotionManagementApi.exportGlobalEntries({
          promotionIds: selectedPromotionKeys.value,
        });
        saveExcelBlob(
          response,
          `선택_프로모션_참여자_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`,
        );
        message.success("참여자 다운로드를 시작했습니다.");
      } catch (error) {
        message.error(error.response?.data?.message || "참여자 다운로드에 실패했습니다.");
      } finally {
        entryExporting.value = false;
      }
    },
  });
};

const downloadScopedEntries = () => {
  if (!hasScopedEntryFilter.value) {
    message.warning("제조사 또는 생성일 범위를 먼저 선택해주세요.");
    return;
  }
  Modal.confirm({
    title: "조건 프로모션 참여자 다운로드",
    content:
      "현재 제조사/생성일 조건에 해당하는 프로모션 참여자를 다운로드합니다. 전화번호와 선택 동의 항목이 포함됩니다.",
    okText: "다운로드",
    cancelText: "취소",
    onOk: async () => {
      entryExporting.value = true;
      try {
        const response = await PromotionManagementApi.exportGlobalEntries({
          manufacturerId: filters.manufacturerId,
          keyword: filters.keyword,
          status: undefined,
          searchField: "all",
          createdFrom: createdRange.value?.[0]?.format("YYYY-MM-DD"),
          createdTo: createdRange.value?.[1]?.format("YYYY-MM-DD"),
        });
        saveExcelBlob(
          response,
          `조건_프로모션_참여자_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`,
        );
        message.success("참여자 다운로드를 시작했습니다.");
      } catch (error) {
        message.error(error.response?.data?.message || "참여자 다운로드에 실패했습니다.");
      } finally {
        entryExporting.value = false;
      }
    },
  });
};

const statusLabel = (status) => {
  const labels = {
    draft: "초안",
    active: "진행중",
    paused: "일시중지",
    closed: "종료",
  };
  return labels[status] || status || "-";
};

const statusColor = (status) => {
  const colors = {
    draft: "default",
    active: "green",
    paused: "gold",
    closed: "red",
  };
  return colors[status] || "default";
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = dayjs(value);
  return date.isValid() ? date.format("YYYY-MM-DD HH:mm") : "-";
};

onMounted(() => {
  loadManufacturers();
  fetchPromotions();
});
</script>

<style scoped>
.promotion-page {
  min-height: 100%;
  padding: 28px 24px 48px;
  background: #f6f8fb;
  color: #1f2937;
}

.promotion-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 18px;
}

.eyebrow {
  margin: 0 0 6px;
  color: #3f6b57;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.promotion-header h1 {
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  letter-spacing: 0;
}

.description {
  margin: 8px 0 0;
  color: #657184;
  font-size: 14px;
}

.header-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.header-actions :deep(.ant-btn) {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 38px;
}

.filter-panel,
.table-panel {
  border: 1px solid #dbe3ed;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(31, 41, 55, 0.04);
}

.filter-panel {
  padding: 18px;
  margin-bottom: 14px;
}

.filter-grid {
  display: grid;
  grid-template-columns: minmax(260px, 1.7fr) minmax(180px, 1fr) 150px minmax(240px, 1.1fr);
  gap: 12px;
  align-items: end;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 7px;
  min-width: 0;
}

.filter-field span {
  color: #526173;
  font-size: 12px;
  font-weight: 700;
}

.filter-field :deep(.ant-select),
.filter-field :deep(.ant-picker) {
  width: 100%;
}

.filter-note {
  margin-top: 12px;
  color: #7a8798;
  font-size: 12px;
}

.table-panel {
  overflow: hidden;
}

.table-summary {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 6px;
  padding: 15px 18px;
  border-bottom: 1px solid #edf1f5;
}

.selected-count {
  padding: 6px 11px;
  border: 1px solid #dbe3ed;
  border-radius: 999px;
  color: #6f7f90;
  background: #f7f9fb;
  font-size: 12px;
  font-weight: 800;
}

.selected-count.active {
  border-color: #9fd2b4;
  color: #214f3b;
  background: #edf8f1;
}

.table-summary strong {
  color: #214f3b;
  font-size: 22px;
}

.table-summary span {
  color: #667386;
  font-size: 13px;
}

.table-panel :deep(.ant-table-wrapper) {
  overflow-x: auto;
}

.table-panel :deep(.ant-table-tbody > tr) {
  cursor: pointer;
}

.table-panel :deep(.ant-table-tbody > tr > td) {
  vertical-align: top;
}

.title-cell {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.title-cell strong,
.title-cell span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.title-cell span {
  margin-top: 3px;
  color: #8a95a5;
  font-size: 12px;
}

.number-cell {
  font-variant-numeric: tabular-nums;
}

@media (max-width: 1200px) {
  .promotion-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .header-actions {
    justify-content: flex-start;
  }

  .filter-grid {
    grid-template-columns: repeat(2, minmax(220px, 1fr));
  }
}

@media (max-width: 720px) {
  .promotion-page {
    padding: 20px 14px 36px;
  }

  .filter-grid {
    grid-template-columns: 1fr;
  }
}
</style>
